<template>
  <div>
    <Header :data="headerData" ></Header>
    <div id="talks" class="bck__linear">
      <div class="container__section">
        <h1 class="title">
          {{ t("heading1") }}
        </h1>
        <h3 class="subtitle" v-html="t('heading2')"></h3>
        <section class="index__section">
          <div class="community__cat">
            <button
              v-for="cat in postsCategory"
              :key="cat"
              class="community__cat--item"
              :class="{
                'community__cat--item-selected': filterSelected === cat,
              }"
              @click="filterPosts(cat)"
            >
              <div class="community__cat--item-logo">
                <Svg
                  class="community__cat--item-svg"
                  :icon="`post-${cat}`"
                ></Svg>
              </div>
              {{ t(`categories.${cat}`) }}
            </button>
          </div>
          <div class="community__actions">
            <div class="community__actions--filters">
              <button
                v-for="sort in sortPosts"
                :key="sort.name"
                class="community__actions--filters-item"
                :class="{
                  'community__actions--filters-item-selected':
                    orderSelected == sort.order,
                }"
                @click="reorderPosts(sort.order)"
              >
                {{ sort.name }}
              </button>
            </div>
            <div class="community__actions--questions">
              <router-link
                :to="{ name: 'ContributionPost' }"
                class="btn btn__spacing btn--blue"
              >
                {{ t("subject") }}
              </router-link>
            </div>
          </div>
          <div class="community__container">
            <template v-for="post in postsData">
              <post-card
                :post="post"
                :key="post._id"
                v-if="
                  post.category === filterSelected || filterSelected === 'all'
                "
              ></post-card>
            </template>
          </div>
        </section>
        <newsletter />
      </div>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent, ref } from "vue";
import Loading from "@/components/utils/Loading";
import PostCard from "@/components/community/PostCard";

import Header from "@/components/Header";
import Svg from "@/components/utils/Svg";

import { dynamicSort } from "@/composables/utils/sort";

import axios from "axios";

import { useI18n } from "vue-i18n";

import en from "@/locales/en/community.json";
import fr from "@/locales/fr/community.json";

const Newsletter = defineAsyncComponent({
  loader: () =>
    import("@/components/Newsletter" /* webpackChunkName: "newsletter" */),
  loadingComponent: Loading,
});

export default {
  components: { Header, Newsletter, PostCard, Svg },
  setup() {
    const postsData = ref([]);
    const postsCategory = ref([]);
    const filterSelected = ref("all");
    const orderSelected = ref("-createdAt");

    const { t, locale } = useI18n({
      messages: {
        en: en,
        fr: fr,
      },
    });

    const headerData = {
      background: "community.jpg",
      title: t("title"),
      subtitle: t("subtitle"),
      layout: "page",
      scroll: "talks",
      actions: [
        {
          title: t("cta"),
          svg: "search",
          scroll: "talks",
          color: "blue",
        },
      ],
    };

    const sortPosts = [
      {
        name: t("sort.latest"),
        order: "-createdAt",
      },
      {
        name: t("sort.popular"),
        order: "likes",
      },

      {
        name: t("sort.old"),
        order: "createdAt",
      },
    ];

    const getPosts = async () => {
      try {
        const res = await axios({
          method: "GET",
          url: "posts",
        });

        postsData.value = res.data.data.data;
        reorderPosts(orderSelected.value);
        postsCategory.value = [
          "all",
          ...new Set(postsData.value.map((el) => el.category)),
        ]; //This function creates an array of all existing categories found from posts
      } catch (err) {
        console.log(err.response);
      }
    };

    //FETCHING DATA
    getPosts();

    const filterPosts = (filter) => {
      filterSelected.value = filter;
    };

    const reorderPosts = (order) => {
      postsData.value.sort(dynamicSort(order));
      orderSelected.value = order;
    };

    return {
      filterPosts,
      filterSelected,
      headerData,
      orderSelected,
      postsCategory,
      postsData,
      reorderPosts,
      sortPosts,
      t,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/style/main";

.community {
  &__container {
    display: flex;
    flex-direction: column;
  }

  &__cat {
    display: flex;
    border-bottom: 2px solid $color-blue-light;
    padding: 2rem 0;
    &--item {
      display: flex;
      flex-direction: column;
      align-items: center;

      @include buttonNoStyle;

      &:not(:last-child) {
        margin-right: 2rem;
      }

      font-size: 1.4rem;
      letter-spacing: 0.1rem;
      text-transform: uppercase;

      &-logo {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 8rem;
        height: 8rem;
        border-radius: 50%;
        border: 2px solid $color-blue-light;
        margin-bottom: 0.6rem;
        color: $color-blue-light;

        box-shadow: $shadow-light;

        transition: all 0.2s;
      }

      &-svg {
        @include svg(4rem);
      }
      &:hover > &-logo {
        background-color: $color-blue-light;
        color: $color-white;
      }

      &-selected > &-logo {
        background-color: $color-blue-light;
        color: $color-white;
      }
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    padding: 2rem 0;
    margin-bottom: 4rem;

    //dashed line
    background-image: linear-gradient(
      to right,
      $color-blue-light 50%,
      rgba(255, 255, 255, 0) 0%
    );
    background-position: bottom;
    background-size: 1rem 2px;
    background-repeat: repeat-x;

    &--filters {
      display: flex;
      &-item {
        @include buttonNoStyle;

        position: relative;
        font-size: 1.4rem;
        letter-spacing: 0.2rem;
        text-transform: uppercase;

        &:not(:last-child) {
          margin-right: 1.5rem;
        }

        @include hoverUnderline;

        &-selected {
          &::after {
            position: absolute;
            content: "";
            top: calc(100% + 0.4rem);
            left: 0;
            width: 100%;
            height: 0.1rem;
            background-color: currentColor;
          }
        }
      }
    }
    &--questions {
      margin-left: auto;
    }
  }
}
</style>
